import React, { useState, useEffect, useRef, useCallback } from "react";
import MuiAlert from '@mui/material/Alert';
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { useActions } from "../../../hooks/useActions";
import DTDataGrid from "../../../components/shared/config/DTDataGrid";
import { IEventBundle } from "../../../models/configuration/bundles/IBundles";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Snackbar,
  TextField,
  Typography,
  Stack,
  Grid
} from "@mui/material";
import ConfigLoad from "../../../components/shared/config/ConfigLoad";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/store";
import EncompassService from "../../../services/encompassService";
import { otherDocColumns, otherDocColumnsRef } from "./configbundlingdata";
import OtherDocumentBundlingDialog from "./OtherDocumentBundlingDialog";
import ConfirmationDialog from "../../../components/shared/ConfirmDialog";
import "../../../assets/style.css";
import { AlertSeverity } from "../../../constants/AlertTypes";
import { SIGNED, OTHER } from "../../../constants/BundlingConstants";
import { IBundleConfiguration } from "../../../models/configuration/bundles/IBundles";
import { DtCard } from "../../../components/ui/card";
import { DtHeader } from "../../../components/ui/header";
import { DtConfirmDialog } from "../../../components/ui/dialog";
import { DtDataGrid } from "../../../components/ui/dataGrid";

interface OtherDocumentBundlingProps {
  updateOtherDocuments: (eventBundles: Array<IEventBundle>) => void;
  docBundles: IBundleConfiguration | null;
  initialEventBundles?: Array<IEventBundle>;
  loading?: boolean;
  error?: string;
}

const OtherDocumentBundling: React.FC<OtherDocumentBundlingProps> = ({ 
  updateOtherDocuments, 
  docBundles, 
  initialEventBundles = [],
  loading = false,
  error
}) => {

  // State - use initialEventBundles prop instead of local state
  const otherDocEventBundles = initialEventBundles;
  const [selectedEventBundle, setSelectedEventBundle] = useState<IEventBundle | null>(null);
  const [editBundlingDialog, setEditBundlingDialog] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState<string>('');

  // State Confirmations
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');

  function editItem( item: any) {
    if (otherDocEventBundles && otherDocEventBundles.length > 0 && item.EventName) {
      const bundle = otherDocEventBundles.filter((bundle) => bundle.EventName === item.EventName);
      if (bundle && bundle[0].Configuration && bundle[0].EventName === item.EventName) {
        setSelectedEventBundle(bundle[0]);
        setEditBundlingDialog(true);
      }
    }
  }

  const deleteItem = (item: any) => {
    setSelectedEventBundle(item);
    setConfirmDialogOpen(true);
  };

  function deleteBundle(item: IEventBundle | null) {
    if (item) {
      // find the newOtherDocEventBundles without the one deleted
      const newOtherDocEventBundles = otherDocEventBundles.filter((bundle) => bundle.EventName !== item.EventName);
      updateOtherDocuments(newOtherDocEventBundles);
      setSelectedEventBundle(null);
      setConfirmDialogOpen(false);
      openAlert(`Bundle Configuration for ${item.EventName} Deleted Successfully`, 'success');
    } else {
      setConfirmDialogOpen(false);
    }
  }

  const updateEventBundle = (eventBundle: IEventBundle) => {
    const updatedBundles = otherDocEventBundles.map((bundle) =>
      bundle.EventName === eventBundle.EventName
        ? { ...bundle, Configuration: eventBundle.Configuration }
        : bundle
    );
    updateOtherDocuments(updatedBundles);
  };

  function cancelDeleteBundle() {
    setConfirmDialogOpen(false);
    setSelectedEventBundle(null);
  }

  // Alert Handling 
  const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
  const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  }

  const closeFxtn = () => {
    setEditBundlingDialog(false);
  };

  const addNewEvent = (newEventName: string) => {
    // use the signed bundle template to create a new bundle
    const otherBundlesData = docBundles?.Configuration?.Bundling
      ?.filter((x) => x.BundlingConfigurationType?.toLowerCase() === SIGNED)
      ?.map((bundle) => ({
        ...bundle,
        BundlingConfigurationType: OTHER // Create new object instead of mutating
      }));

    const newBundle: IEventBundle = {
      EventName: newEventName?.trim(),
      Configuration: otherBundlesData && otherBundlesData.length > 0
        ? otherBundlesData : []
    };

    if (newBundle && newBundle.Configuration) {
      const IsBundleAlreadyExists = otherDocEventBundles.some(
        (bundle) => bundle?.EventName?.toLowerCase() === newEventName?.trim()?.toLowerCase()
      );
      if (IsBundleAlreadyExists) {
        openAlert(`Bundling Configuration for ${newEventName} already exists`, 'error');
      } else {
        // Updated main state via parent callback
        const updatedBundles = [...otherDocEventBundles, newBundle];
        updateOtherDocuments(updatedBundles);
        openAlert(`New bundle for ${newEventName} added successfully`, 'success');
        setNewEvent('');
      }
    }
  }

  // --- UI ---
  return (
    <>
      <ConfigLoad loading={loading} />
      {error ? (
        <Box sx={{
          mx: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          background: "#fff",
          p: 3
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            Unable to load data
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Box>
      ) : (
        <Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <DtCard header='Add Event Bundle' sx={{ minHeight: '11rem', width: '100%' }}>
                <Stack direction="column" spacing={3}>
                  <Stack direction="column" spacing={2}>
                    <TextField
                      label="Add Event Name"
                      variant="outlined"
                      size="small"
                      value={newEvent}
                      onChange={(e) => setNewEvent(e.target.value)}
                      helperText="Event names must be at least 6 characters"
                      sx={{ mt: 3 }}
                    />
                    <Button
                      onClick={() => addNewEvent(newEvent)}
                      variant="contained"
                      color="primary"
                      disableElevation
                      disabled={!newEvent || newEvent.length < 6}
                      size="small"
                      sx={{ maxWidth: '100px' }}
                    >
                      Add Event
                    </Button>
                  </Stack>
                </Stack>
              </DtCard>
            </Grid>
            <Grid item xs={12} md={8}>
              <DtCard header='Event Bundles' >
                <DtDataGrid
                  data={otherDocEventBundles}
                  columns={otherDocColumnsRef()}
                  tableName="otherdocs"
                  editConfig={{
                    mode: 'custom',
                    onEdit(row) {
                      editItem(row);
                    },
                  }}
                  deleteConfig={{
                    onDelete(row) {
                      deleteItem(row);
                    },
                  }}
                  paged
                  responsivePageSize
                  responsivePageConfig={{heightRatio: 0.5}}
                />
              </DtCard>
            </Grid>
          </Grid>

          <OtherDocumentBundlingDialog
            eventBundle={selectedEventBundle}
            open={editBundlingDialog}
            closeFxtn={closeFxtn}
            updateEventBundle={updateEventBundle}
          />

            <DtConfirmDialog
              maxWidth="xs"
              open={confirmDialogOpen}
              cancelLabel="No"
              confirmLabel="Delete"
              confirmColor="primary"
              title="Delete Event"
              message={`Are you sure you want to delete the Configuration for '${selectedEventBundle?.EventName}'?`}
              onConfirm={() => deleteBundle(selectedEventBundle)}
              onClose={cancelDeleteBundle}
            />

          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={alertOpen}
            autoHideDuration={4000}
            onClose={handleSnackBarClose}
          >
            <MuiAlert
              onClose={handleSnackBarClose}
              severity={alertSeverity}
              sx={{ width: '100%', fontSize: '1.1rem', py: 1.5, px: 2 }}
            >
              {alertMessage}
            </MuiAlert>
          </Snackbar>
        </Box>
      )}
    </>
  );
};

export default OtherDocumentBundling;